import { useEffect, useRef, useState } from 'react';
import { Canvas, FabricObject, FabricImage, Rect, Line, Ellipse, Triangle, Textbox, IText, Group, Path, loadSVGFromString, config as fabricConfig } from 'fabric';
import { CurvedIText } from '../../utils/CurvedIText';
import { useBuilderState } from '../../hooks/useBuilderState';
import { useFabricCanvas } from '../../hooks/useFabricCanvas';
import { useBuilderStore } from '../../store/builderStore';
import { ColorizedSvg } from '../ColorizedSvg';
import { createImageNode, createSvgNode, createTextNode, createShapeNode } from '../../utils/nodeUtils';
import { fetchSvgTextAndColors } from '../../utils/svgUtils';
import { buildCurvePath, getCurveVisualBounds, getAutoPathStartOffset } from '../../utils/curveUtils';
import type { CurvePreset } from '../../utils/curveUtils';
import { runValidation } from '../../lib/validation';
import { loadGoogleFont, normalizeWeight } from '../../lib/fonts';
import { TextDebugOverlay } from '../TextDebugOverlay';
import { DpiIndicator } from '../DpiIndicator';
import { isBuilderDebug } from '../../lib/debugFlags';
import type { DesignNode, ImageNodeData, ShapeNodeData, TextNodeData, SvgNodeData } from '../../types/node.types';

// Force at least 2× backing resolution so text and shapes render crisp,
// even on non-retina displays where window.devicePixelRatio is 1.
fabricConfig.devicePixelRatio = Math.max(window.devicePixelRatio, 2);

// Tag for non-interactive overlay objects (safe area guides, border)
const OVERLAY_TAG = '__overlay__';

/** Fabric object extended with a custom `id` property for node tracking */
type TaggedFabricObject = FabricObject & { id?: string; tag?: string };

// ─── Module-level helpers (no React deps, live outside component) ────────────

const triggerNodeValidation = (obj: FabricObject & { id?: string }) => {
  if (!obj.id || obj.type !== 'image') return;
  const state = useBuilderStore.getState();
  const node = (state.surfaces[state.currentSurface] ?? []).find((n: DesignNode) => n.id === obj.id);
  if (!node) return;
  const dims = state.productConfig.dimensions[state.currentSurface];
  if (!dims) return;
  // Get the underlying HTMLImageElement for natural dimensions (actual DPI)
  const imageElement = obj instanceof FabricImage
    ? obj.getElement() as HTMLImageElement
    : undefined;
  runValidation('scale', {
    node,
    surface: state.currentSurface,
    canvasDimensions: dims,
    productConfig: state.productConfig,
    imageElement,
    placedRect: {
      x: obj.left ?? 0, y: obj.top ?? 0,
      width: (obj.width ?? 0) * (obj.scaleX ?? 1),
      height: (obj.height ?? 0) * (obj.scaleY ?? 1),
    },
  }).then(results => {
    state.setNodeValidation(obj.id!, results);
  });
};

const pushObjectToZustand = (obj: FabricObject & { id?: string }) => {
  if (!obj.id) return;
  const { updateNode, surfaces, currentSurface } = useBuilderStore.getState();
  const scaleX = obj.scaleX ?? 1;
  const scaleY = obj.scaleY ?? 1;

  // Text objects: scale font size instead of dimensions when resized
  if (obj.type === 'textbox' || obj.type === 'i-text') {
    const textObj = obj as unknown as IText;
    if (Math.abs(scaleY - 1) > 0.01 || Math.abs(scaleX - 1) > 0.01) {
      const newFontSize = Math.max(1, Math.round(textObj.fontSize * scaleY));
      const newWidth = (textObj.width ?? 0) * scaleX;
      textObj.set({ fontSize: newFontSize, width: newWidth, scaleX: 1, scaleY: 1 });

      const node = (surfaces[currentSurface] ?? []).find((n: DesignNode) => n.id === obj.id);
      const textData = node?.data as TextNodeData | undefined;
      const dataUpdate: Partial<TextNodeData> = { fontSize: newFontSize };

      // Curved text: regenerate the path to match the new scaled dimensions
      if (textData?.curvePathData && textData.curvePreset && textData.curvePreset !== 'none') {
        const preset = textData.curvePreset as CurvePreset;
        const newRadius = preset === 'circle' && textData.curveRadius
          ? textData.curveRadius * Math.max(scaleX, scaleY)
          : textData.curveRadius;
        const newPathData = buildCurvePath(preset, newWidth, textData.curveAmount ?? 50, newRadius);

        if (newPathData) {
          const debug = isBuilderDebug();
          const curvePath = new Path(newPathData, { visible: debug, selectable: false, evented: false, stroke: debug ? 'red' : '', fill: 'transparent' });
          const pathSide = 'left';
          textObj.set({ path: curvePath, pathStartOffset: textData.pathStartOffset ?? 0, pathSide, pathAlign: 'center' } as Partial<IText>);
        }

        dataUpdate.curvePathData = newPathData;
        dataUpdate.curveRadius = newRadius;
      }

      textObj.initDimensions();

      // Recalculate padding for curved text
      if (textData?.curvePreset && textData.curvePreset !== 'none') {
        const bounds = getCurveVisualBounds(
          textData.curvePreset as CurvePreset, newWidth, newFontSize,
          textData.curveAmount ?? 50, dataUpdate.curveRadius ?? textData.curveRadius,
        );
        const padX = Math.max(0, (bounds.width - (textObj.width ?? 0)) / 2);
        const padY = Math.max(0, (bounds.height - (textObj.height ?? 0)) / 2);
        textObj.set({ padding: Math.ceil(Math.max(padX, padY)) });
      }

      updateNode(obj.id!, {
        position: { x: obj.left ?? 0, y: obj.top ?? 0 },
        size: { width: newWidth, height: textObj.height ?? 0 },
        rotation: obj.angle ?? 0,
        opacity: obj.opacity ?? 1,
        data: { ...(textData as TextNodeData), ...dataUpdate },
      });
      return;
    }
  }

  updateNode(obj.id, {
    position: { x: obj.left ?? 0, y: obj.top ?? 0 },
    size: {
      width: (obj.width ?? 0) * scaleX,
      height: (obj.height ?? 0) * scaleY,
    },
    rotation: obj.angle ?? 0,
    opacity: obj.opacity ?? 1,
  });
  // Normalise scale back to 1 so width/height stay canonical.
  // Skip for images and SVG groups: their width/height are natural (intrinsic)
  // dimensions, so resetting scale to 1 would snap them to full natural size.
  if (obj.type !== 'image' && obj.type !== 'group') {
    const newW = (obj.width ?? 0) * scaleX;
    const newH = (obj.height ?? 0) * scaleY;
    if (obj.type === 'ellipse') {
      (obj as unknown as Ellipse).set({ rx: newW / 2, ry: newH / 2 });
    } else {
      obj.set({ width: newW, height: newH });
    }
    obj.set({ scaleX: 1, scaleY: 1 });
  }
};

const drawOverlays = (
  canvas: Canvas,
  dimensions: { width: number; height: number }
) => {
  // Remove stale overlays first
  canvas.getObjects()
    .filter((o) => (o as TaggedFabricObject).tag === OVERLAY_TAG)
    .forEach(o => canvas.remove(o));

  const s = 2;
  const inset = s / 2;
  const w = dimensions.width;
  const h = dimensions.height;

  // Calculate a dash pattern that fits a given length evenly,
  // ensuring dashes always land on corners.
  const fitDash = (length: number) => {
    const targetDash = Math.max(4, Math.min(length, h, w) / 50);
    const targetGap = targetDash * 0.7;
    const n = Math.max(1, Math.round((length + targetGap) / (targetDash + targetGap)));
    const gap = n > 1 ? targetGap : 0;
    const dash = n > 1 ? (length - (n - 1) * gap) / n : length;
    return [dash, gap];
  };

  const lineProps = {
    stroke: '#A8B8F0', strokeWidth: s,
    selectable: false, evented: false, excludeFromExport: true,
    hoverCursor: 'default',
  } as const;

  // Top, Right, Bottom, Left
  const sides: [number, number, number, number, number][] = [
    [inset, inset, w - inset, inset, w - s],       // top
    [w - inset, inset, w - inset, h - inset, h - s], // right
    [w - inset, h - inset, inset, h - inset, w - s], // bottom
    [inset, h - inset, inset, inset, h - s],         // left
  ];

  for (const [x1, y1, x2, y2, len] of sides) {
    const line = new Line([x1, y1, x2, y2], {
      ...lineProps,
      strokeDashArray: fitDash(len),
    });
    (line as TaggedFabricObject).tag = OVERLAY_TAG;
    canvas.add(line);
  }
};

/** Reorder Fabric objects so they match node zIndex order */
const reorderCanvasByZIndex = (canvas: Canvas) => {
  const state = useBuilderStore.getState();
  const nodes = state.surfaces[state.currentSurface] ?? [];
  const zIndexMap = new Map<string, number>();
  nodes.forEach(n => zIndexMap.set(n.id, n.zIndex));

  const objects = canvas.getObjects() as TaggedFabricObject[];
  const userObjects = objects.filter(o => o.tag !== OVERLAY_TAG && o.id);
  const sorted = [...userObjects].sort((a, b) => {
    const zA = zIndexMap.get(a.id!) ?? 0;
    const zB = zIndexMap.get(b.id!) ?? 0;
    return zA - zB;
  });

  sorted.forEach(obj => canvas.bringObjectToFront(obj));
  objects.filter(o => o.tag === OVERLAY_TAG).forEach(o => canvas.bringObjectToFront(o));
  canvas.renderAll();
};

const addFabricImage = (canvas: Canvas, node: DesignNode) => {
  const data = node.data as ImageNodeData;

  const htmlImg = new Image();
  htmlImg.crossOrigin = 'anonymous';
  htmlImg.onload = () => {
    const fImg = new FabricImage(htmlImg);
    const naturalW = fImg.width || node.size.width;
    const naturalH = fImg.height || node.size.height;
    const scale = Math.min(node.size.width / naturalW, node.size.height / naturalH);
    fImg.set({
      left: node.position.x,
      top: node.position.y,
      angle: node.rotation,
      opacity: node.opacity,
      visible: node.visible,
      evented: node.visible,
      scaleX: scale,
      scaleY: scale,
    });
    (fImg as TaggedFabricObject).id = node.id;
    canvas.add(fImg);
    reorderCanvasByZIndex(canvas);

    // Run placement-stage DPI validation
    const state = useBuilderStore.getState();
    const dims = state.productConfig.dimensions[state.currentSurface];
    if (dims) {
      runValidation('placement', {
        node,
        surface: state.currentSurface,
        canvasDimensions: dims,
        productConfig: state.productConfig,
        imageElement: htmlImg,
        placedRect: {
          x: fImg.left ?? 0, y: fImg.top ?? 0,
          width: (fImg.width ?? 0) * (fImg.scaleX ?? 1),
          height: (fImg.height ?? 0) * (fImg.scaleY ?? 1),
        },
      }).then(results => {
        state.setNodeValidation(node.id, results);
      });
    }
  };
  htmlImg.onerror = () => {
    console.warn('[BuilderCanvas] Failed to load image:', data.src);
  };
  htmlImg.src = data.src;
};

const addFabricShape = (canvas: Canvas, node: DesignNode) => {
  const data = node.data as ShapeNodeData;
  const common = {
    left: node.position.x,
    top: node.position.y,
    angle: node.rotation,
    opacity: node.opacity,
    visible: node.visible,
    evented: node.visible,
    fill: data.fill,
    stroke: data.stroke,
    strokeWidth: data.strokeWidth ?? 0,
  };

  let obj: FabricObject;
  switch (data.shapeType) {
    case 'circle':
      obj = new Ellipse({
        ...common,
        rx: node.size.width / 2,
        ry: node.size.height / 2,
      });
      break;
    case 'triangle':
      obj = new Triangle({ ...common, width: node.size.width, height: node.size.height });
      break;
    default:
      obj = new Rect({ ...common, width: node.size.width, height: node.size.height });
  }

  (obj as TaggedFabricObject).id = node.id;
  canvas.add(obj);
};

const addFabricText = async (canvas: Canvas, node: DesignNode) => {
  const data = node.data as TextNodeData;
  const weight = normalizeWeight(data.fontWeight);

  // Ensure the specific font+weight is loaded before creating the Fabric object
  await loadGoogleFont(data.fontFamily, weight);

  const displayText = data.uppercase ? data.text.toUpperCase() : data.lowercase ? data.text.toLowerCase() : data.text;

  const commonProps = {
    left: node.position.x,
    top: node.position.y,
    angle: node.rotation,
    opacity: node.opacity,
    visible: node.visible,
    evented: node.visible,
    fontSize: data.fontSize,
    fontFamily: data.fontFamily,
    fontWeight: data.fontWeight ?? 400,
    fontStyle: data.fontStyle ?? 'normal',
    fill: data.fill,
    textAlign: data.textAlign ?? 'left',
    underline: data.underline ?? false,
    charSpacing: data.charSpacing ?? 0,
    lineHeight: data.lineHeight ?? 1.16,
  };

  // Use CurvedIText for curved text (path-aware cursor/selection rendering),
  // Textbox for straight text (supports word wrapping).
  let textObj: CurvedIText | Textbox;
  if (data.curvePathData) {
    const debug = isBuilderDebug();
    const curvePath = new Path(data.curvePathData, { visible: debug, selectable: false, evented: false, stroke: debug ? 'red' : '', fill: 'transparent' });
    const pathSide = 'left';
    const autoOffset = data.pathStartOffset != null && data.pathStartOffset !== 0 ? data.pathStartOffset : getAutoPathStartOffset(
      (data.curvePreset ?? 'none') as CurvePreset,
      displayText,
      commonProps.fontSize ?? data.fontSize ?? 40,
      commonProps.fontFamily ?? data.fontFamily ?? 'Arial',
      commonProps.fontWeight ?? data.fontWeight ?? 400,
      commonProps.fontStyle ?? data.fontStyle ?? 'normal',
      commonProps.charSpacing ?? data.charSpacing ?? 0,
      node.size.width,
      data.curveAmount ?? 50,
      data.curveRadius,
    );
    textObj = new CurvedIText(displayText, { ...commonProps, path: curvePath, pathStartOffset: autoOffset, pathSide, pathAlign: 'center', objectCaching: false });
    // Compute exact padding from curve geometry so bounding box matches rendered text
    textObj.initDimensions();
    const fs = commonProps.fontSize ?? data.fontSize ?? 40;
    const textW = textObj.width || node.size.width;
    const bounds = getCurveVisualBounds((data.curvePreset ?? 'none') as CurvePreset, textW, fs, data.curveAmount ?? 50, data.curveRadius);
    const padX = Math.max(0, (bounds.width - textObj.width) / 2);
    const padY = Math.max(0, (bounds.height - textObj.height) / 2);
    textObj.set({ padding: Math.ceil(Math.max(padX, padY)) });
  } else {
    textObj = new Textbox(displayText, {
      ...commonProps,
      width: node.size.width,
      breakWords: data.breakWords ?? true,
    });
  }

  (textObj as TaggedFabricObject).id = node.id;

  // Push updated text content to Zustand when user finishes inline editing.
  // Curved text keeps its path during editing so the cursor follows the curve.
  if (data.curvePathData) {
    textObj.on('editing:exited', () => {
      const { updateNode } = useBuilderStore.getState();
      const currentNode = useBuilderStore.getState().surfaces[
        useBuilderStore.getState().currentSurface
      ]?.find((n: DesignNode) => n.id === node.id);
      updateNode(node.id, { data: { ...(currentNode?.data ?? data), text: textObj.text ?? '' } });
    });
  } else {
    textObj.on('editing:exited', () => {
      const { updateNode } = useBuilderStore.getState();
      const currentNode = useBuilderStore.getState().surfaces[
        useBuilderStore.getState().currentSurface
      ]?.find((n: DesignNode) => n.id === node.id);
      updateNode(node.id, { data: { ...(currentNode?.data ?? data), text: textObj.text ?? '' } });
    });
  }

  canvas.add(textObj);
};

const addFabricSvg = async (canvas: Canvas, node: DesignNode) => {
  const data = node.data as SvgNodeData;

  try {
    const { svgText } = await fetchSvgTextAndColors(data.src);
    const { objects } = await loadSVGFromString(svgText);
    const validObjects = objects.filter((o): o is FabricObject => o !== null);

    // Apply single fill color to all non-transparent paths
    validObjects.forEach((obj) => {
      const currentFill = (obj as FabricObject & { fill?: string }).fill;
      if (currentFill && currentFill !== 'none' && currentFill !== 'transparent') {
        obj.set({ fill: data.fill });
      }
    });

    const group = new Group(validObjects);
    const naturalW = group.width || node.size.width;
    const naturalH = group.height || node.size.height;
    const scale = Math.min(node.size.width / naturalW, node.size.height / naturalH);

    group.set({
      left: node.position.x,
      top: node.position.y,
      angle: node.rotation,
      opacity: node.opacity,
      visible: node.visible,
      evented: node.visible,
      scaleX: scale,
      scaleY: scale,
    });

    (group as TaggedFabricObject).id = node.id;
    canvas.add(group);
    reorderCanvasByZIndex(canvas);

    // Run placement-stage validation for SVG
    const state = useBuilderStore.getState();
    const dims = state.productConfig.dimensions[state.currentSurface];
    if (dims) {
      runValidation('placement', {
        node,
        surface: state.currentSurface,
        canvasDimensions: dims,
        productConfig: state.productConfig,
        placedRect: {
          x: group.left ?? 0, y: group.top ?? 0,
          width: (group.width ?? 0) * (group.scaleX ?? 1),
          height: (group.height ?? 0) * (group.scaleY ?? 1),
        },
      }).then(results => {
        state.setNodeValidation(node.id, results);
      });
    }
  } catch (err) {
    console.warn('[BuilderCanvas] Failed to load SVG:', data.src, err);
  }
};

const loadNodes = async (canvas: Canvas, nodes: DesignNode[]) => {
  const sorted = [...nodes].sort((a, b) => a.zIndex - b.zIndex);
  await Promise.all(sorted.map(async (node) => {
    switch (node.type) {
      case 'image': addFabricImage(canvas, node); break;
      case 'shape': addFabricShape(canvas, node); break;
      case 'text':  await addFabricText(canvas, node); break;
      case 'svg':   await addFabricSvg(canvas, node); break;
    }
  }));
  canvas.renderAll();
};

// ─── Component ───────────────────────────────────────────────────────────────

const BuilderCanvas = () => {
  const htmlCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const { canvasRef } = useFabricCanvas();
  const initializedRef = useRef(false);
  const knownNodeIdsRef = useRef<Set<string>>(new Set());

  const {
    currentSurface,
    productConfig,
    surfaces,
    selectedNodeId,
    selectNode,
    addNode,
    importVersion,
    historyVersion,
  } = useBuilderState();

  const selectedColor = useBuilderStore((s) => s.selectedColor);

  const surfaceDimensions = productConfig.dimensions[currentSurface];

  // ── Responsive container sizing (must be above effects that use canvasScale) ──
  const outerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState(720);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const rect = entries[0]?.contentRect;
      if (rect) {
        const size = Math.min(rect.width, rect.height);
        setContainerSize(size);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const widthRatio = productConfig.canvasWidthRatio?.[currentSurface] ?? 0.5;
  const canvasScale = (containerSize * widthRatio) / surfaceDimensions.width;

  // ── Init: create canvas once on mount ────────────────────────────────────
  useEffect(() => {
    if (!htmlCanvasRef.current || initializedRef.current) return;
    initializedRef.current = true;

    const canvas = new Canvas(htmlCanvasRef.current, {
      width: surfaceDimensions.width,
      height: surfaceDimensions.height,
      selection: true,
      preserveObjectStacking: true,
      backgroundColor: '',
    });
    canvasRef.current = canvas;

    // ── Selection / bounding-box styling ──────────────────────────────────
    const selectionStyle = {
      borderColor: '#2563eb',
      cornerColor: '#fff',
      cornerStrokeColor: '#2563eb',
      cornerSize: 7,
      cornerStyle: 'rect' as const,
      transparentCorners: false,
      borderScaleFactor: 1,
      padding: 1,
    };
    Object.assign(FabricObject.ownDefaults, selectionStyle);
    canvas.selectionColor = 'rgba(37, 99, 235, 0.08)';
    canvas.selectionBorderColor = '#2563eb';
    canvas.selectionLineWidth = 1.5;

    // Fabric → Zustand: selection events
    canvas.on('selection:created', (e) => {
      const obj = e.selected?.[0] as TaggedFabricObject | undefined;
      if (obj?.id) {
        useBuilderStore.getState().selectNode(obj.id);
        triggerNodeValidation(obj);
      }
    });

    canvas.on('selection:updated', (e) => {
      const obj = e.selected?.[0] as TaggedFabricObject | undefined;
      if (obj?.id) {
        useBuilderStore.getState().selectNode(obj.id);
        triggerNodeValidation(obj);
      }
    });

    canvas.on('selection:cleared', () => {
      useBuilderStore.getState().selectNode(null);
    });

    // Fabric → Zustand: transform/move
    canvas.on('object:modified', (e: { target: FabricObject }) => {
      const obj = e.target as FabricObject & { id?: string };
      pushObjectToZustand(obj);
      triggerNodeValidation(obj);
    });

    drawOverlays(canvas, surfaceDimensions);

    // Re-measure all text objects whenever a font finishes loading.
    // Safety net for edge cases where the font binary arrives after the text
    // object was already added to the canvas (slow CDN, browser cache race, etc.).
    // We only recalculate internal dimensions (line breaks, height) — we never
    // override the stored width, which would destroy user-set or saved sizes.
    const handleFontLoadingDone = () => {
      const c = canvasRef.current;
      if (!c) return;
      c.getObjects().forEach((obj) => {
        if (obj.type === 'textbox' || obj.type === 'i-text') {
          const tObj = obj as Textbox;
          tObj.initDimensions();
          tObj.setCoords();
        }
      });
      c.requestRenderAll();
    };
    document.fonts.addEventListener('loadingdone', handleFontLoadingDone);

    return () => {
      document.fonts.removeEventListener('loadingdone', handleFontLoadingDone);
      canvas.dispose();
      canvasRef.current = null;
      initializedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Surface switch / import: resize canvas + reload nodes ────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Preserve overlay objects, remove everything else
    const overlays = canvas.getObjects().filter((o) => (o as TaggedFabricObject).tag === OVERLAY_TAG);
    // Remove all objects, then re-add overlays
    [...canvas.getObjects()].forEach(o => canvas.remove(o));
    overlays.forEach(o => canvas.add(o));

    canvas.setDimensions({ width: surfaceDimensions.width, height: surfaceDimensions.height });

    drawOverlays(canvas, surfaceDimensions);
    const nodes = surfaces[currentSurface] || [];
    knownNodeIdsRef.current = new Set(nodes.map(n => n.id));
    loadNodes(canvas, nodes);
  }, [currentSurface, importVersion, historyVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync new nodes from Zustand → Fabric (e.g. click-to-add, upload) ────
  const currentNodes = surfaces[currentSurface] || [];
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const newNodes = currentNodes.filter(n => !knownNodeIdsRef.current.has(n.id));
    if (newNodes.length === 0) return;

    // Mark IDs synchronously to prevent duplicate additions on rapid state changes
    newNodes.forEach(n => knownNodeIdsRef.current.add(n.id));

    const addNewNodes = async () => {
      await Promise.all(newNodes.map(async (n) => {
        switch (n.type) {
          case 'image': addFabricImage(canvas, n); break;
          case 'shape': addFabricShape(canvas, n); break;
          case 'text':  await addFabricText(canvas, n); break;
          case 'svg':   await addFabricSvg(canvas, n); break;
        }
      }));
      reorderCanvasByZIndex(canvas);
    };
    addNewNodes();
  }, [currentNodes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync zIndex order: reorder Fabric objects to match node zIndex ──────
  // Handles layer panel drag-reorder and any other zIndex changes in the store.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    reorderCanvasByZIndex(canvas);
  }, [currentNodes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync external selection → Fabric (LayerPanel clicks) ─────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!selectedNodeId) {
      canvas.discardActiveObject();
      canvas.renderAll();
      return;
    }

    const target = canvas.getObjects().find((o) => (o as TaggedFabricObject).id === selectedNodeId);
    if (target && canvas.getActiveObject() !== target) {
      canvas.setActiveObject(target);
      canvas.renderAll();
    }
  }, [selectedNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click outside canvas → deselect ──────────────────────────────────────
  const handleOuterClick = (e: React.MouseEvent) => {
    if (canvasContainerRef.current?.contains(e.target as Node)) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.discardActiveObject();
    canvas.renderAll();
    selectNode(null);
  };

  // ── Drop handler: from DesignLibrary or LayerPanel element tools ─────────
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const rawData = e.dataTransfer.getData('text/plain');
    if (!rawData) return;

    try {
      const dropData = JSON.parse(rawData);
      const canvas = canvasRef.current;
      if (!canvas) return;

      const canvasEl = canvas.getElement();
      const rect = canvasEl.getBoundingClientRect();

      const currentLayers = surfaces[currentSurface] || [];
      const maxZIndex = currentLayers.length > 0
        ? Math.max(...currentLayers.map(n => n.zIndex))
        : 0;

      // Shape or text element from LayerPanel tool buttons
      if (dropData.type === 'text') {
        const x = (e.clientX - rect.left) / canvasScale - 100;
        const y = (e.clientY - rect.top) / canvasScale - 25;
        const node = createTextNode(currentSurface, productConfig.type, { x, y });
        node.zIndex = maxZIndex + 1;
        addNode(node);
        return;
      }

      if (dropData.type === 'shape') {
        const shapeSize = {
          width: Math.min(150, surfaceDimensions.width * 0.3),
          height: Math.min(150, surfaceDimensions.height * 0.3),
        };
        const x = (e.clientX - rect.left) / canvasScale - shapeSize.width / 2;
        const y = (e.clientY - rect.top) / canvasScale - shapeSize.height / 2;
        const node = createShapeNode(dropData.shapeType, currentSurface, productConfig.type, { x, y }, shapeSize);
        node.zIndex = maxZIndex + 1;
        addNode(node);
        return;
      }

      // Image/SVG asset from DesignLibrary
      const rawW = dropData.width || 200;
      const rawH = dropData.height || 200;
      const maxW = surfaceDimensions.width * 0.6;
      const maxH = surfaceDimensions.height * 0.6;
      const fitScale = Math.min(maxW / rawW, maxH / rawH, 1);
      const fittedW = rawW * fitScale;
      const fittedH = rawH * fitScale;

      const x = (e.clientX - rect.left) / canvasScale - fittedW / 2;
      const y = (e.clientY - rect.top) / canvasScale - fittedH / 2;

      const position = { x, y };
      const size = { width: fittedW, height: fittedH };

      if (dropData.mimeType === 'image/svg+xml') {
        const { originalColors } = await fetchSvgTextAndColors(dropData.src);
        const node = createSvgNode(dropData.src, currentSurface, 't-shirt', position, size, originalColors, maxZIndex + 1);
        addNode(node);
      } else {
        const node = createImageNode(dropData.src, currentSurface, 't-shirt', position, size, maxZIndex + 1);
        addNode(node);
      }
    } catch {
      // malformed drag data — ignore
    }
  };

  const mockupSrc = productConfig.mockups?.[currentSurface];
  const offset = productConfig.canvasOffset?.[currentSurface] ?? { x: 0, y: 0 };
  const scaledWidth = surfaceDimensions.width * canvasScale;
  const scaledHeight = surfaceDimensions.height * canvasScale;
  // Offsets are pixel values relative to the container size
  const canvasLeft = (containerSize - scaledWidth) / 2 + offset.x * (containerSize / 720);
  const canvasTop = (containerSize - scaledHeight) / 2 + offset.y * (containerSize / 720);

  return (
    <div
      ref={outerRef}
      className="flex-1 flex items-center justify-center bg-gray-100 min-h-0 min-w-0 p-24"
      onDragOver={e => e.preventDefault()}
      onDrop={handleDrop}
      onClick={handleOuterClick}
    >
      <div className="relative" style={{ width: containerSize, height: containerSize }}>
        {mockupSrc && (
          <ColorizedSvg
            src={mockupSrc}
            bodyColor={selectedColor?.hex ?? '#FFFFFF'}
            outlineColor={selectedColor?.outlineHex ?? '#000000'}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none [&>svg]:w-full [&>svg]:h-full"
          />
        )}
        <div
          ref={canvasContainerRef}
          className="absolute"
          style={{ left: canvasLeft, top: canvasTop, transform: `scale(${canvasScale})`, transformOrigin: 'top left' }}
        >
          <canvas ref={htmlCanvasRef} />
        </div>
        {isBuilderDebug() && <TextDebugOverlay />}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <DpiIndicator />
        </div>
      </div>
    </div>
  );
};

export { BuilderCanvas };
